import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { waLink, WhatsAppIcon } from "@/lib/site";
import { T, useLang } from "@/lib/i18n";

const IMAGES = {
  city: "/images/salzburg-street.webp",
  airport: "/images/salzburg-airport.webp",
  tours: "/images/mirabell.webp",
  ski: "/images/alps-ski.webp",
};

export default function Services() {
  const { lang } = useLang();
  const t = T[lang].services;

  return (
    <section id="services" data-testid="services-section" className="relative py-24 lg:py-36 overflow-hidden">
      <img src="/images/services-bg.webp" alt="" aria-hidden="true" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-[#16110C]/45" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#16110C] via-[#16110C]/25 to-[#16110C]" />
      <div className="relative max-w-7xl mx-auto px-5 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="max-w-2xl"
        >
          <p className="font-mono-label text-xs uppercase tracking-[0.28em] text-[#C5A059] mb-4">{t.overline}</p>
          <h2 className="font-serif-display font-semibold tracking-tight text-stone-100 text-3xl sm:text-4xl lg:text-5xl">
            {t.h2a}<em className="not-italic text-[#C5A059]">{t.h2b}</em>{t.h2c}
          </h2>
        </motion.div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {t.cards.map((s, i) => (
            <motion.article
              key={s.testid}
              data-testid={`service-card-${s.testid}`}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.1, ease: "easeOut" }}
              whileHover={{ y: -8, scale: 1.03 }}
              className="group relative rounded-3xl overflow-hidden bg-[#292219] border border-stone-800 shadow-sm hover:shadow-xl hover:shadow-black/40 hover:border-[#C5A059]/30 transition-all duration-500 flex flex-col"
            >
              <div className="relative overflow-hidden aspect-[16/10]">
                <img
                  src={IMAGES[s.testid]}
                  alt={s.title}
                  loading="lazy" decoding="async"
                  className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#292219] via-transparent to-transparent" />
              </div>
              <div className="p-7 flex flex-col flex-1">
                <h3 className="font-serif-display font-semibold text-2xl text-stone-100 mb-3">{s.title}</h3>
                <p className="text-stone-400 leading-relaxed text-[15px] flex-1">{s.text}</p>
                <a
                  data-testid={`service-whatsapp-${s.testid}`}
                  href={waLink(s.wa)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-[#C5A059] group-hover:gap-3 transition-all duration-300"
                >
                  <WhatsAppIcon className="w-4 h-4" />
                  {t.cta}
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
